import React, { useCallback, useEffect, useState } from "react";
import { Button } from "devextreme-react/button";
import PopUp, { ToolbarItem } from "devextreme-react/popup";
import PropTypes from "prop-types";
import {
  ResourceBackBtnSVG,
  ResourceVersionActive,
  ResourceVersionInActive,
} from "../../Assets/SVGs/ResourceSVGs";
import OrganisationDetailsDataGrid from "../OrganisationDetailsDataGrid/OrganisationDetailsDataGrid";
import ResourceVersionList from "../ResourceVersionsList/ResourceVersionList";
import Notification from "../Notification/Notification";
import { useUser } from "../../contexts/UserContext";
import "./ViewResource.scss";
import { getResourceTypeDisplayName } from "../../utilities";
import { selectedResourcePropTypes } from "../../Constants/propTypes";

const deprecateText =
  "Deprecating means you and your connected organizations will lose access to all the versions of the resource as well as any future updates to it.";
const deprecateQues = "Deprecate this resource ?";

const ViewResource = ({
  setIsViewResourcePageVisible,
  selectedResource,
  setSelectedResourceCallback,
}) => {
  const { backend } = useUser();
  const initialActiveResourceVersion =
    selectedResource.resourceVersions.find((version) => version.active) ||
    selectedResource.resourceVersions[0];

  const [selectedResourceVersion, setSelectedResourceVersion] = useState(
    initialActiveResourceVersion
  );
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  useEffect(() => {
    const activeResourceVersion = selectedResource.resourceVersions.find(
      (version) => version.active
    );
    setSelectedResourceVersion(activeResourceVersion);
  }, [selectedResource.resourceVersions]);

  const closePopups = useCallback(() => {
    setIsPopupVisible(false);
  }, []);

  const deactivateSelectedResource = useCallback(() => {
    const updatedSelectedResource = {
      ...selectedResource,
      active: false,
    };

    setSelectedResourceCallback(updatedSelectedResource);
  }, [selectedResource, setSelectedResourceCallback]);

  const handleDeprecateResource = useCallback(() => {
    backend
      .callDeprecateResource(selectedResource.id)
      .then((res) => {
        setIsPopupVisible(false);
        if (res.succeeded) {
          Notification(res.message, "success");
          setIsViewResourcePageVisible(false);
          deactivateSelectedResource();
        } else {
          Notification(res.message, "error");
        }
      })
      .catch((res) => {
        console.error(res);
      });
  }, [
    backend,
    deactivateSelectedResource,
    selectedResource.id,
    setIsViewResourcePageVisible,
  ]);

  const getCancelOptions = useCallback(
    () => ({
      text: "Cancel",
      onClick: closePopups,
    }),
    [closePopups]
  );

  const getConfirmDeprecateOptions = useCallback(
    () => ({
      text: "Yes, deprecate",
      onClick: handleDeprecateResource,
      hint: "Confirm deprecate resource",
    }),
    [handleDeprecateResource]
  );

  const renderDeprecateResourcePopupContent = () => (
    <div>
      <div className="text-align-center">
        <span className="deprecate-text">{deprecateText}</span>
        <div>
          <span className="deprecate-question">{deprecateQues}</span>
        </div>
      </div>
    </div>
  );

  const handleBackButtonClick = useCallback(() => {
    setIsViewResourcePageVisible(false);
  }, [setIsViewResourcePageVisible]);

  return (
    <div className="resource-div ">
      <div className="resource-header">
        <PopUp
          visible={isPopupVisible}
          onhiding={closePopups}
          showCloseButton={false}
          showTitle={true}
          title="Deprecate resource"
          position="center"
          maxHeight={250}
          maxWidth={430}
          contentRender={renderDeprecateResourcePopupContent}
        >
          <ToolbarItem
            options={getCancelOptions()}
            widget="dxButton"
            location="after"
            toolbar="bottom"
            cssClass="cancel-button"
          />
          <ToolbarItem
            options={getConfirmDeprecateOptions()}
            widget="dxButton"
            location="after"
            toolbar="bottom"
            cssClass="deprecate-resource-button"
          />
        </PopUp>
        <div
          style={{
            flexBasis: "3%",
            paddingTop: "20px",
            textAlign: "center",
          }}
        >
          <Button onClick={handleBackButtonClick} stylingMode="text">
            {ResourceBackBtnSVG}
          </Button>
        </div>
        <div style={{ flexBasis: "94%" }}>
          <div className="resource-version-info ">
            <div>
              <span id="viewresource-span-resource-popover">{selectedResource.name}</span>{" "}
            </div>
            <div>
              <span className="res-details-span">
                {getResourceTypeDisplayName(selectedResource.resourceTypeId)} | By: (
                {selectedResource.organizationName}) |
              </span>
              <span
                className="text-success"
                style={{
                  fontWeight: "600",
                }}
              >
                {" "}
                {selectedResource.active ? (
                  <span>{ResourceVersionActive} Active</span>
                ) : (
                  <span className="text-danger">
                    {ResourceVersionInActive} Deprecated
                  </span>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="resource-body h-100 ">
        <div className="resources-version-list ">
          <ResourceVersionList
            setSelectedResourceVersion={setSelectedResourceVersion}
            selectedResource={selectedResource}
            selectedResourceVersion={selectedResourceVersion}
          />
        </div>
        <div className="w-100 org-grid-list ">
          {selectedResourceVersion && (
            <OrganisationDetailsDataGrid
              selectedResource={selectedResource}
              selectedResourceVersion={selectedResourceVersion}
              setSelectedResourceCallback={setSelectedResourceCallback}
            />
          )}
        </div>
      </div>
    </div>
  );
};

ViewResource.propTypes = {
  setIsViewResourcePageVisible: PropTypes.func.isRequired,
  selectedResource: selectedResourcePropTypes,
  setSelectedResourceCallback: PropTypes.func.isRequired,
};

export default ViewResource;
